/**
 * ★★ WE — A BAIXA AUTOMÁTICA DA CONSULTA AO MOVER A ETAPA. Puro.
 *
 * É a decisão ESPELHADA de `ETAPA_AO_CANCELAR` (`agenda/cancelamento.js`), que faz o caminho inverso:
 * cancelar uma consulta decide a etapa do lead. Aqui, mover a etapa decide o status da consulta. As duas
 * moram lado a lado de propósito — quem mexer numa tem de ver a outra.
 *
 * ★ POR QUE ISTO EXISTE: até a WE, mover o lead para qualquer etapa **deixava a consulta aberta para
 * sempre** (`docs/we-medicao.md` §2 — `moverEtapa` não tinha uma única linha de agenda). O passivo medido
 * no dia do conserto: **71 leads** em etapa pós-comparecimento com avaliação ainda aberta, a mais antiga de
 * fevereiro/2026.
 */

/**
 * ★★ A LISTA DE "QUEM VEIO" É PRÓPRIA, E NÃO REUSA `ETAPAS_POS_COMPARECIMENTO`. ISTO É DECISÃO.
 *
 * `ETAPAS_POS_COMPARECIMENTO` (`@/leads/cadencia`) é `['compareceu', 'nao_fechou']` e responde a outra
 * pergunta: **"quem o negociador trabalha"**. Ela NÃO inclui `fechado` — e faz sentido que não inclua, no
 * uso dela: quem fechou saiu da fila do negociador.
 *
 * Aqui a pergunta é **"a pessoa sentou na cadeira?"**, e a resposta inclui `fechado`. Medido: dos 71 leads
 * com avaliação aberta, **24 estão em `fechado`** — reusar aquela constante deixaria de dar baixa
 * justamente em quem fechou, que é o caso que mais dói.
 *
 * ★ QUEM "LIMPAR A DUPLICAÇÃO" TROCANDO ESTE MAPA POR `ETAPAS_POS_COMPARECIMENTO` ESTÁ DESFAZENDO UMA
 * DECISÃO, não removendo repetição. Há guarda de fonte (`we.guardas.test.js`) que falha se este arquivo
 * importar aquela constante, e a mutação que tira `fechado` daqui (E3) morre num teste puro.
 */
export const STATUS_DA_BAIXA_POR_ETAPA = {
  // A pessoa VEIO — a consulta aconteceu.
  compareceu: 'compareceu',
  nao_fechou: 'compareceu',
  fechado: 'compareceu',
  // A pessoa NÃO VEIO.
  nao_compareceu: 'faltou',
  // ★ Decisão do dono: se o lead voltou para "não agendou", aquela consulta deixou de ser um compromisso.
  nao_agendou: 'cancelado',
  // `novo` e `agendado` estão AUSENTES de propósito: mover para eles não resolve consulta nenhuma.
  // `agendado` em especial — é onde a consulta ACABOU de ser marcada.
};

/** O status com que a consulta aberta deve ser fechada ao mover o lead para `etapa`. `null` = não mexe. */
export const statusDaBaixa = (etapa) => STATUS_DA_BAIXA_POR_ETAPA[etapa] ?? null;

/**
 * ★ SÓ CONSULTAS DE AVALIAÇÃO RECEBEM BAIXA AUTOMÁTICA — e isto também é decisão, não descuido.
 *
 * Medido no dia do conserto: das **743 consultas abertas em produção, 743 são `avaliacao`** — zero
 * `retorno` pendurado. Mas o código precisa decidir para o dia em que houver, e a decisão é **não tocar**:
 * um `retorno` é um compromisso **futuro**, e a etapa do lead não o resolve. Fechar a venda não significa
 * que a pessoa compareceu ao retorno de dezembro; marcá-lo como "comparecido" inventaria um fato.
 *
 * A mutação que amplia isto para todos os tipos (E10) morre num teste que deixa um `retorno` aberto.
 */
export const RECEBE_BAIXA_AUTOMATICA = 'avaliacao';

/**
 * As consultas que a mudança de etapa deve fechar.
 *
 * ★ TODAS as abertas, não só a mais recente. Medido: **54 leads têm mais de uma consulta aberta** (47 com
 * duas, 6 com três, 1 com cinco). Fechar só uma deixaria a outra pendurada e reproduziria o defeito no
 * clique seguinte — que é exatamente o caminho que esta peça existe para fechar.
 */
export const consultasParaBaixar = (agendamentos = []) =>
  (agendamentos ?? []).filter(
    (a) => a?.status === 'agendado' && a?.tipo === RECEBE_BAIXA_AUTOMATICA,
  );
